import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useRouteError,
} from "@remix-run/react";

// Custom
import "./styles/global.css";
import "./styles/reset.css";
import {
  SectionNotFound,
  SectionNotFoundProps,
} from "./components/sections/SectionNotFound";
import { useNonce } from "@shopify/hydrogen";
import { json, LoaderFunctionArgs } from "@remix-run/cloudflare";

export function links() {
  return [
    {
      rel: "preconnect",
      href: "https://fonts.googleapis.com",
    },
    {
      rel: "preconnect",
      href: "https://fonts.gstatic.com",
    },
    {
      rel: "stylesheet",
      href: "https://fonts.googleapis.com/css2?family=Comic+Neue:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap",
    },
  ];
}

export async function loader({ request, context }: LoaderFunctionArgs) {
  const searchParams = new URL(request.url).searchParams;
  const gtmDisable = searchParams.get("gtmDisable");

  return json({
    gtmDisable,
    gtmContainerId: context.cloudflare.env.GOOGLE_TAG_MANAGER_CONTAINER_ID,
  });
}

export function Layout({ children }: { children: React.ReactNode }) {
  const nonce = useNonce();
  const data = useLoaderData<typeof loader>();

  const hasGtm = !data?.gtmDisable && data?.gtmContainerId;

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Journi</title>
        <meta
          name="description"
          content="Journi is a free interactive road safety education program for Years 5&6."
        />
        <meta property="og:image" content="/open-graph.jpg" />
        {hasGtm && (
          <script
            dangerouslySetInnerHTML={{
              __html: `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${data?.gtmContainerId}');
              `,
            }}
            suppressHydrationWarning
            nonce={nonce}
          />
        )}
        <Meta />
        <Links />
      </head>
      <body>
        {hasGtm && (
          <noscript
            dangerouslySetInnerHTML={{
              __html: `
              <iframe src="https://www.googletagmanager.com/ns.html?id=${data?.gtmContainerId}"
              height="0" width="0" style="display:none;visibility:hidden"></iframe>
            `,
            }}
          />
        )}
        {children}
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  );
}

function App() {
  return <Outlet />;
}

export default withSentry(App);

export function ErrorBoundary() {
  const error = useRouteError();
  const location = useLocation();

  let args: SectionNotFoundProps = {
    text: "Unknown Error",
    link: {
      to: location.pathname,
      title: "Try refreshing page",
      replace: true,
    },
    layout: "right" as "left" | "right",
  };

  if (isRouteErrorResponse(error)) {
    args.text = `${error.status || error.data.status} ${error.statusText || error.data.statusText}`;

    if (error.status === 404) {
      args = {
        ...args,
        ...{
          link: undefined,
          backgroundSrc: {
            src: "https://imagedelivery.net/5u-BnzY4d8R245ADRh1vnA/33194cf5-01af-497b-3f6e-662c76d2c200/public",
          },
          errorImageSrc: {
            src: "https://imagedelivery.net/5u-BnzY4d8R245ADRh1vnA/9c5042a8-7bfc-4b8d-97a8-cd2505d68900/public",
          },
          layout: "left" as const,
        },
      };
    }

    if (error?.data?.referer) {
      args.link = {
        ...args.link,
        ...{
          to: error.data.referer,
          title: "Go back",
        },
      };
    }

    // Log extra information
    console.error(error.data);
  } else if (error instanceof Error) {
    args.text = error.message;

    // Log extra information
    console.error(error.stack);
  }

  captureRemixErrorBoundaryError(error);

  return <SectionNotFound {...args} />;
}
